/**
 * ============================================================================
 * Iron Meridian - Action Queue Component
 * ============================================================================
 * 
 * Shows queued actions for the current turn with ability to cancel them.
 * Premium design with clear action indicators and smooth animations.
 */

export default function ActionQueue({ actions, countries, onRemoveAction, onClearActions }) {
  if (actions.length === 0) {
    return (
      <div className="p-5 border-b border-war-room-600/30">
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-2">
          <span>📋</span>
          Action Queue
          <span className="text-xs bg-war-room-700/80 px-2 py-0.5 rounded-full text-gray-500">0</span>
        </h2>
        <div className="text-center py-6">
          <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-war-room-700/30 flex items-center justify-center">
            <span className="text-xl opacity-30">📋</span>
          </div>
          <p className="text-sm text-gray-500">No actions queued</p>
          <p className="text-xs text-gray-600 mt-1">Actions will appear here</p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-5 border-b border-war-room-600/30">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2">
          <span>📋</span>
          Action Queue
          <span className="text-xs bg-war-room-accent px-2.5 py-0.5 rounded-full text-white font-bold">
            {actions.length}
          </span>
        </h2>
        <button
          onClick={onClearActions}
          className="text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 px-2 py-1 rounded-lg transition-all"
        >
          Clear All
        </button>
      </div>

      {/* Action List */}
      <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
        {actions.map((action, index) => (
          <ActionItem
            key={action.id}
            action={action}
            index={index}
            countries={countries}
            onRemove={() => onRemoveAction(action.id)}
          />
        ))}
      </div>
    </div>
  )
}

function ActionItem({ action, index, countries, onRemove }) {
  const actionConfig = {
    attack: { icon: '⚔️', color: 'border-red-500', bg: 'bg-red-500/10', text: 'text-red-400' },
    build: { icon: '🏗️', color: 'border-amber-500', bg: 'bg-amber-500/10', text: 'text-amber-400' },
    research: { icon: '🔬', color: 'border-blue-500', bg: 'bg-blue-500/10', text: 'text-blue-400' },
    fortify: { icon: '🏰', color: 'border-green-500', bg: 'bg-green-500/10', text: 'text-green-400' },
  }

  const config = actionConfig[action.type] || { icon: '❓', color: 'border-gray-500', bg: 'bg-gray-500/10', text: 'text-gray-400' }
  const targetName = action.target ? (countries[action.target]?.name || action.target) : 'N/A'

  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl border-l-4 ${config.color} ${config.bg} animate-fade-in group hover:bg-war-room-700/30 transition-colors`}>
      {/* Order Number */}
      <div className="w-7 h-7 bg-war-room-700/80 rounded-lg flex items-center justify-center text-xs font-bold text-gray-400">
        {index + 1}
      </div>

      {/* Action Icon */}
      <span className="text-xl">{config.icon}</span>

      {/* Action Details */}
      <div className="flex-1 min-w-0">
        <div className={`font-medium text-sm capitalize ${config.text}`}>{action.type}</div>
        <div className="text-xs text-gray-500 truncate">
          {action.type === 'attack' && `→ ${targetName}`}
          {action.type === 'build' && `${action.building}`}
          {action.type === 'research' && 'Technology +5'}
          {action.type === 'fortify' && 'Defense +10%'}
        </div>
      </div>

      {/* Remove Button */}
      <button
        onClick={onRemove}
        className="w-7 h-7 rounded-lg bg-war-room-700/50 hover:bg-red-600 flex items-center justify-center text-xs text-gray-400 hover:text-white transition-all opacity-0 group-hover:opacity-100"
        title="Cancel action"
      >
        ✕
      </button>
    </div>
  )
}
